import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  ShieldCheck, FileText, ListChecks, CalendarClock, Mail, AlertTriangle,
  ArrowRight, Send, ChevronRight, Loader2, UserCheck, Building2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import FlagToggle from '../components/FlagToggle';
import HussarTopNav from '../components/HussarTopNav';
import IntegrityCapability from '../components/IntegrityCapability';
import ReviewSignalsSection from '../components/ReviewSignalsSection';
import { track } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DlaAdministracji = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    track('gov_page_view', {});
    document.title = t('gov.admin.metaTitle');
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', t('gov.admin.metaDescription'));
  }, [t]);

  const scrollDemo = () => {
    track('gov_demo_started', { source: 'cta' });
    document.getElementById('gov-demo')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="hussar-landing" data-testid="gov-admin-page">
      <HussarTopNav active="compliance" />
      <header className="hussar-landing-header">
        <div className="hussar-landing-header-content">
          <Link to="/" className="hussar-logo-full">
            <img src="/hussarai-logo-full.png" alt="HussarAi" className="hussar-logo-full-img-small" />
          </Link>
          <div className="hussar-landing-header-actions">
            <Link to="/bezpieczenstwo-i-zgodnosc" className="hussar-tc-back" data-testid="gov-admin-nav-security">
              {t('gov.footer.security')}
            </Link>
            <Link to="/pilot-publiczny" className="hussar-tc-back" data-testid="gov-admin-nav-pilot">
              {t('gov.footer.pilot')}
            </Link>
            <FlagToggle />
          </div>
        </div>
      </header>

      <main className="hussar-agenci-main">
        <section className="hussar-agenci-hero" data-testid="gov-admin-hero">
          <div className="hussar-agenci-eyebrow">
            <Building2 className="w-3 h-3 inline-block mr-1" />
            {t('gov.admin.eyebrow')}
          </div>
          <h1 className="hussar-agenci-h1" data-testid="gov-admin-h1">{t('gov.admin.h1')}</h1>
          <p className="hussar-agenci-subhead" data-testid="gov-admin-subhead">{t('gov.admin.subhead')}</p>
          <p className="hussar-agenci-proof hussar-proof-strong" data-testid="gov-admin-trust">
            {t('gov.admin.trustLine')}
          </p>
          <div className="hussar-agenci-cta-row">
            <Button size="lg" onClick={scrollDemo} className="hussar-btn-primary hussar-btn-large" data-testid="gov-admin-cta-demo">
              {t('gov.admin.ctaDemo')} <ArrowRight className="w-5 h-5" />
            </Button>
            <Button size="lg" variant="outline"
                    onClick={() => track('institution_brief_download_clicked', {})}
                    data-testid="gov-admin-cta-brief">
              {t('gov.admin.ctaBrief')} <ChevronRight className="w-5 h-5" />
            </Button>
            <Button size="lg" variant="outline"
                    onClick={() => navigate('/pilot-publiczny')}
                    data-testid="gov-admin-cta-pilot">
              {t('gov.admin.ctaPilot')} <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </section>

        <section className="hussar-pol-op-section" data-testid="gov-admin-doctrine">
          <h2 className="hussar-agenci-h2">{t('gov.admin.doctrine.title')}</h2>
          <p className="hussar-pol-op-body">{t('gov.admin.doctrine.body')}</p>
        </section>

        <section className="hussar-pol-op-section" data-testid="gov-admin-workflow">
          <h2 className="hussar-agenci-h2">{t('gov.admin.workflow.title')}</h2>
          <ol className="hussar-pol-op-bullets" style={{ listStyle: 'decimal', paddingLeft: '24px' }}>
            {t('gov.admin.workflow.steps', { returnObjects: true }).map((s, i) => (
              <li key={s} data-testid={`gov-admin-step-${i}`}>{s}</li>
            ))}
          </ol>
        </section>

        {/* v4.0 Phase 5 — Case-preparation section for immigration records. */}
        <section className="hussar-pol-op-section" data-testid="gov-admin-case-prep-v4">
          <h2 className="hussar-agenci-h2" data-testid="gov-admin-case-prep-v4-title">
            <ListChecks className="w-6 h-6 inline-block mr-2" />
            {t('gov.admin.casePrepV4.title')}
          </h2>
          <p className="hussar-pol-op-body" data-testid="gov-admin-case-prep-v4-intro">
            {t('gov.admin.casePrepV4.intro')}
          </p>
          <ul className="hussar-pol-op-bullets" data-testid="gov-admin-case-prep-v4-list">
            {(t('gov.admin.casePrepV4.items', { returnObjects: true }) || []).map((item, i) => (
              <li key={item} data-testid={`gov-admin-case-prep-v4-${i}`}>{item}</li>
            ))}
          </ul>
          <p className="hussar-pol-op-body" data-testid="gov-admin-case-prep-v4-human-decision"
             style={{ marginTop: '18px', fontWeight: 600, color: 'rgba(226,232,240,0.92)' }}>
            {t('gov.admin.casePrepV4.humanDecisionLine')}
          </p>
          <p className="hussar-pol-op-body" data-testid="gov-admin-case-prep-v4-integrity"
             style={{ marginTop: '8px', fontStyle: 'italic', color: 'rgba(226,232,240,0.82)' }}>
            {t('gov.admin.casePrepV4.integrityLine')}
          </p>
        </section>

        <section className="hussar-pol-op-section" data-testid="gov-admin-usecases">
          <h2 className="hussar-agenci-h2">{t('gov.admin.useCases.title')}</h2>
          <div className="hussar-agenci-deliverables-grid">
            {t('gov.admin.useCases.cards', { returnObjects: true }).map((card, i) => {
              const Icon = [FileText, ListChecks, CalendarClock, Mail, ShieldCheck, FileText][i] || FileText;
              return (
                <Card key={card.title} className="hussar-agenci-deliverable-card" data-testid={`gov-admin-uc-${i}`}>
                  <div className="hussar-agenci-deliverable-icon"><Icon className="w-5 h-5" /></div>
                  <h3 className="hussar-agenci-deliverable-title" style={{ fontSize: '15px' }}>{card.title}</h3>
                  <p className="hussar-agenci-deliverable-body">{card.body}</p>
                </Card>
              );
            })}
          </div>
        </section>

        <section className="hussar-pol-op-section" data-testid="gov-admin-teams">
          <h2 className="hussar-agenci-h2">{t('gov.admin.teams.title')}</h2>
          <ul className="hussar-pol-op-bullets">
            {t('gov.admin.teams.items', { returnObjects: true }).map((item, i) => (
              <li key={item} data-testid={`gov-admin-team-${i}`}>{item}</li>
            ))}
          </ul>
          <p className="hussar-pol-op-body" style={{ marginTop: '20px', fontStyle: 'italic' }}
             data-testid="gov-admin-strong">
            {t('gov.admin.strong')}
          </p>
        </section>

        <GovTriageDemo />
        <IntegrityCapability testIdPrefix="gov-admin-integrity" />
      </main>
    </div>
  );
};

const initial = {
  institutionType: '', caseType: '', caseDescription: '', documents: '',
  knownDates: '', teamQuestion: '', email: '', consent: false,
};

const GovTriageDemo = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [output, setOutput] = useState(null);
  const [error, setError] = useState('');
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setOutput(null);
    if (!(form.institutionType.trim() || form.caseType.trim() || form.caseDescription.trim()
          || form.documents.trim() || form.teamQuestion.trim())) {
      setError(t('gov.admin.demo.errorValidation'));
      return;
    }
    setSubmitting(true);
    track('gov_demo_submitted', {});
    try {
      const r = await axios.post(`${API}/gov-triage/demo`, form);
      setOutput(r.data);
      track('gov_output_generated', { submissionId: r.data.submissionId });
    } catch (e) {
      setError(e?.response?.data?.detail || t('gov.admin.demo.errorSubmit'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="gov-demo" className="hussar-pol-op-section" data-testid="gov-demo-section">
      <h2 className="hussar-agenci-h2">{t('gov.admin.demo.title')}</h2>
      <p className="hussar-pol-op-body">{t('gov.admin.demo.intro')}</p>
      <Card className="hussar-tc-card hussar-compliance-form" data-testid="gov-demo-form">
        <form onSubmit={submit}>
          <Field label={t('gov.admin.demo.institutionLabel')}>
            <Input value={form.institutionType} onChange={(e) => upd('institutionType', e.target.value)}
                   placeholder={t('gov.admin.demo.institutionPlaceholder')} data-testid="gov-demo-institutionType" />
          </Field>
          <Field label={t('gov.admin.demo.caseTypeLabel')}>
            <Input value={form.caseType} onChange={(e) => upd('caseType', e.target.value)}
                   placeholder={t('gov.admin.demo.caseTypePlaceholder')} data-testid="gov-demo-caseType" />
          </Field>
          <Field label={t('gov.admin.demo.caseDescLabel')}>
            <Textarea rows={4} value={form.caseDescription} onChange={(e) => upd('caseDescription', e.target.value)}
                      placeholder={t('gov.admin.demo.caseDescPlaceholder')} data-testid="gov-demo-caseDescription" />
          </Field>
          <Field label={t('gov.admin.demo.documentsLabel')}>
            <Textarea rows={2} value={form.documents} onChange={(e) => upd('documents', e.target.value)}
                      placeholder={t('gov.admin.demo.documentsPlaceholder')} data-testid="gov-demo-documents" />
          </Field>
          <Field label={t('gov.admin.demo.datesLabel')}>
            <Textarea rows={2} value={form.knownDates} onChange={(e) => upd('knownDates', e.target.value)}
                      placeholder={t('gov.admin.demo.datesPlaceholder')} data-testid="gov-demo-knownDates" />
          </Field>
          <Field label={t('gov.admin.demo.teamQuestionLabel')}>
            <Textarea rows={2} value={form.teamQuestion} onChange={(e) => upd('teamQuestion', e.target.value)}
                      placeholder={t('gov.admin.demo.teamQuestionPlaceholder')} data-testid="gov-demo-teamQuestion" />
          </Field>
          <Field label={t('gov.admin.demo.emailLabel')}>
            <Input type="email" value={form.email} onChange={(e) => upd('email', e.target.value)}
                   data-testid="gov-demo-email" />
          </Field>
          <div className="hussar-compliance-consent">
            <Checkbox id="gov-consent" checked={form.consent} onCheckedChange={(v) => upd('consent', !!v)}
                      data-testid="gov-demo-consent" />
            <label htmlFor="gov-consent">{t('gov.admin.demo.consentLabel')}</label>
          </div>
          {error && <p className="hussar-compliance-error" data-testid="gov-demo-error">{error}</p>}
          <div style={{ marginTop: '16px' }}>
            <Button type="submit" size="lg" className="hussar-btn-primary"
                    disabled={submitting} data-testid="gov-demo-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? t('gov.admin.demo.submitting') : t('gov.admin.demo.submit')}
            </Button>
          </div>
        </form>
      </Card>
      {output && <GovOutput output={output} />}
    </section>
  );
};

const Field = ({ label, children }) => (
  <div className="hussar-cb-field">
    <label className="hussar-form-label">{label}</label>{children}
  </div>
);

// Module-scope helper — hoisted out of GovOutput so React doesn't see a
// fresh component type on every parent render (avoids subtree re-mount).
const GovSection = ({ icon: Icon, label, testid, children }) => (
  <div className="hussar-compliance-output-section" data-testid={testid}>
    <div className="hussar-compliance-output-section-label"><Icon className="w-4 h-4" /> {label}</div>
    <div className="hussar-compliance-output-section-body">{children}</div>
  </div>
);

const GovOutput = ({ output }) => {
  const { t } = useTranslation();
  return (
    <Card className="hussar-tc-card hussar-compliance-output" data-testid="gov-output">
      <div className="hussar-compliance-output-badge" data-testid="gov-output-human-badge">
        <UserCheck className="w-4 h-4" /> {t('gov.admin.output.humanReviewBadge')}
      </div>
      <GovSection icon={FileText}      label={t('gov.admin.output.summaryLabel')}    testid="gov-output-summary"><p>{output.summary}</p></GovSection>
      <GovSection icon={ListChecks}    label={t('gov.admin.output.gapsLabel')}       testid="gov-output-gaps">
        <ul className="hussar-pol-op-bullets">{output.formalGaps.map((g) => <li key={g}>{g}</li>)}</ul>
      </GovSection>
      <GovSection icon={CalendarClock} label={t('gov.admin.output.datesLabel')}      testid="gov-output-dates">
        {output.datesAndDeadlines.length === 0 ? <p className="hussar-tc-meta">—</p>
          : <ul className="hussar-pol-op-bullets">{output.datesAndDeadlines.map((d) => <li key={d}>{d}</li>)}</ul>}
      </GovSection>
      <GovSection icon={ShieldCheck}   label={t('gov.admin.output.checklistLabel')}  testid="gov-output-checklist">
        <ul className="hussar-pol-op-bullets">{output.humanChecklist.map((c) => <li key={c}>{c}</li>)}</ul>
      </GovSection>
      <GovSection icon={Mail}          label={t('gov.admin.output.draftLabel')}      testid="gov-output-draft">
        <pre className="hussar-compliance-output-draft">{output.draftResponse}</pre>
      </GovSection>
      <ReviewSignalsSection
        signals={output.reviewSignals}
        framing={output.reviewSignalsFraming}
        disclaimer={output.reviewSignalsDisclaimer}
        testidPrefix="gov-output"
      />
      <GovSection icon={AlertTriangle} label={t('gov.admin.output.disclaimerLabel')} testid="gov-output-disclaimer">
        <p className="hussar-pol-op-body">{output.disclaimer}</p>
      </GovSection>
    </Card>
  );
};

export default DlaAdministracji;
